import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, TrendingUp } from "lucide-react";
import { BrandIcon } from "@/components/BrandIcon";
import { CloseOldEnquiries } from "@/components/CloseOldEnquiries";
import { SourceBackfill } from "@/components/SourceBackfill";
import { useAuth } from "@/context/AuthContext";

const rate = (r) => (r.total ? `${Math.round((r.won / r.total) * 100)}%` : "—");
const monthLabel = (m) => new Date(`${m}-01`).toLocaleDateString("en-GB", { month: "short", year: "numeric" });

function Row({ label, r, testid, icon }) {
  return (
    <tr className="border-t border-slate-100" data-testid={testid}>
      <td className="py-2 pr-3 text-slate-800"><span className="inline-flex items-center gap-2">{icon}{label}</span></td>
      <td className="py-2 px-3 text-right tabular-nums">{r.total}</td>
      <td className="py-2 px-3 text-right tabular-nums text-emerald-700">{r.won}</td>
      <td className="py-2 px-3 text-right tabular-nums text-rose-600">{r.abandoned}</td>
      <td className="py-2 px-3 text-right tabular-nums text-slate-500">{r.open}</td>
      <td className="py-2 pl-3 text-right tabular-nums font-medium">{rate(r)}</td>
    </tr>
  );
}

function Table({ title, children, testid }) {
  return (
    <section className="bg-white border border-slate-200 rounded-[8px] p-5 overflow-x-auto" data-testid={testid}>
      <h2 className="text-base font-semibold text-slate-900 mb-2">{title}</h2>
      <table className="w-full text-sm min-w-[520px]">
        <thead><tr className="text-[11px] uppercase tracking-wide text-slate-500">
          <th className="text-left font-semibold py-1.5 pr-3">&nbsp;</th><th className="text-right font-semibold px-3">Enquiries</th>
          <th className="text-right font-semibold px-3">Won</th><th className="text-right font-semibold px-3">Abandoned</th>
          <th className="text-right font-semibold px-3">Still open</th><th className="text-right font-semibold pl-3">Win rate</th>
        </tr></thead>
        <tbody>{children}</tbody>
      </table>
    </section>
  );
}

function MonthRow({ m }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => setOpen((o) => !o)} data-testid={`eo-month-${m.month}`}>
        <td className="py-2 pr-3 font-medium text-slate-900">{monthLabel(m.month)} <span className="text-[11px] text-indigo-600">{open ? "hide sources" : "by source"}</span></td>
        <td className="py-2 px-3 text-right tabular-nums">{m.total}</td>
        <td className="py-2 px-3 text-right tabular-nums text-emerald-700">{m.won}</td>
        <td className="py-2 px-3 text-right tabular-nums text-rose-600">{m.abandoned}</td>
        <td className="py-2 px-3 text-right tabular-nums text-slate-500">{m.open}</td>
        <td className="py-2 pl-3 text-right tabular-nums font-medium">{rate(m)}</td>
      </tr>
      {open && m.sources.map((s) => (
        <Row key={s.source} label={<span className="pl-4 text-slate-600">{s.source}</span>} r={s} icon={<BrandIcon name={s.source} size={14} />} testid={`eo-month-${m.month}-${s.source}`} />
      ))}
    </>
  );
}

export default function EnquiryOutcomesPage() {
  const [d, setD] = useState(null);
  const [months, setMonths] = useState(12);
  const [tick, setTick] = useState(0);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super;
  useEffect(() => { setD(null); api.get("/enquiry-outcomes/report", { params: { months } }).then((r) => setD(r.data)).catch(() => setD({ months: [], sources: [] })); }, [months, tick]);
  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8 space-y-5" data-testid="enquiry-outcomes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3"><PageIcon /> Enquiry Outcomes</h1>
          <p className="text-sm text-slate-500 mt-1">Enquiries won or abandoned each month, by enquiry source.</p>
        </div>
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="h-10 rounded-[8px] border border-slate-200 bg-white px-3 text-sm" data-testid="eo-months">
          {[3, 6, 12, 24].map((n) => <option key={n} value={n}>Last {n} months</option>)}
        </select>
      </div>
      {!d ? <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></div> : (
        <>
          <Table title="By enquiry source" testid="eo-sources">
            {d.sources.length ? d.sources.map((s) => <Row key={s.source} label={s.source} r={s} icon={<BrandIcon name={s.source} size={16} />} testid={`eo-source-${s.source}`} />)
              : <tr><td colSpan={6} className="py-6 text-center text-slate-400">No enquiries in this period.</td></tr>}
          </Table>
          <Table title="By month" testid="eo-months-table">
            {d.months.map((m) => <MonthRow key={m.month} m={m} />)}
          </Table>
          {d.rules && <p className="text-[11px] text-slate-500" data-testid="eo-rules">Won: {d.rules.won}. Abandoned: {d.rules.abandoned}. Month: {d.rules.month}.</p>}
          {isAdmin && <SourceBackfill onChanged={() => setTick((x) => x + 1)} />}
          {isAdmin && <CloseOldEnquiries onChanged={() => setTick((x) => x + 1)} />}
        </>
      )}
    </div>
  );
}
